import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { userService } from "../services/userService";
import { kycService } from "../services/kycService";
import { currentAppMode, isStockTradingMode } from "../config";
import MainHeader from "../components/MainHeader";
import { capitalize } from "lodash";

interface AdminDashboardProps {
  user: any;
  onLogout: () => void;
  onNavigateToUserManagement: () => void;
  onNavigateToAppUserManagement: () => void;
  onNavigateToChallengeManagement: () => void;
  onNavigateToAuditLogs: () => void;
  onNavigateToKYCManagement: () => void;
  onNavigateToWithdrawalManagement: () => void;
  onNavigateToBannerManagement: () => void;
  onNavigateToStockManagement: () => void;
  onNavigateToStockUniverseManagement: () => void;
  onNavigateToCryptoManagement: () => void;
  onNavigateToCryptoUniverseManagement: () => void;
  onNavigateToSportsLegalDocs: () => void;
  onNavigateToHelpSupport: () => void;
}

// Modern Admin Dashboard component
export default function AdminDashboard({
  user,
  onLogout,
  onNavigateToUserManagement,
  onNavigateToAppUserManagement,
  onNavigateToChallengeManagement,
  onNavigateToAuditLogs,
  onNavigateToKYCManagement,
  onNavigateToWithdrawalManagement,
  onNavigateToBannerManagement,
  onNavigateToHelpSupport,
  onNavigateToStockManagement,
  onNavigateToStockUniverseManagement,
  onNavigateToCryptoManagement,
  onNavigateToCryptoUniverseManagement,
  onNavigateToSportsLegalDocs,
}: AdminDashboardProps) {
  const [recentActivityData, setRecentActivityData] = useState<any>(null);
  const [recentActivityLoading, setRecentActivityLoading] = useState(false);
  const [recentActivityError, setRecentActivityError] = useState("");

  // Pagination state for audit logs
  const [auditLogsPage, setAuditLogsPage] = useState(1);
  const [auditLogsPerPage] = useState(10);

  // Toggle state for Recent Activity section
  const [isRecentActivityExpanded, setIsRecentActivityExpanded] =
    useState(true);

  console.log("recentActivityData-----------------------", recentActivityData);

  useEffect(() => {
    const fetchRecentActivity = async () => {
      try {
        const data = await userService.getRecentActivity();
        setRecentActivityData(data?.recent_activity);
      } catch (err: any) {
        console.error("Error fetching recent activity:", err);
        setRecentActivityError(
          err.message || "Failed to fetch recent activity"
        );
      } finally {
        setRecentActivityLoading(false);
      }
    };

    fetchRecentActivity();
  }, []);

  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // KYC Analytics State
  const [kycAnalytics, setKycAnalytics] = useState<any>(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState("");
  const [kycStartDate, setKycStartDate] = useState("");
  const [kycEndDate, setKycEndDate] = useState("");
  console.log("kycAnalytics-----------------------", kycAnalytics);
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const data = await userService.getDashboardMetrics();
        setMetrics(data);
      } catch (err: any) {
        console.error("Error fetching metrics:", err);
        setError(err.message || "Failed to fetch dashboard metrics");
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const fetchKycAnalytics = async (start?: string, end?: string) => {
    setKycLoading(true);
    setKycError("");
    try {
      const data = await kycService.getKYCAnalytics(start, end);
      setKycAnalytics(data);
    } catch (err: any) {
      setKycError(err.message || "Failed to fetch KYC analytics");
    } finally {
      setKycLoading(false);
    }
  };

  useEffect(() => {
    fetchKycAnalytics();
  }, []);

  const handleLogout = () => {
    onLogout();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getActionBadgeColor = (action: string) => {
    switch (action.toLowerCase()) {
      case "create":
        return "bg-green-100 text-green-800";
      case "update":
        return "bg-blue-100 text-blue-800";
      case "delete":
        return "bg-red-100 text-red-800";
      case "cancel":
        return "bg-yellow-100 text-yellow-800";
      case "kyc_approved":
        return "bg-green-100 text-green-800";
      case "kyc_rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getResourceTypeBadgeColor = (resourceType: string) => {
    switch (resourceType.toLowerCase()) {
      case "stock_data":
        return "bg-blue-100 text-blue-800";
      case "promotional_banner":
        return "bg-purple-100 text-purple-800";
      case "challenge":
        return "bg-orange-100 text-orange-800";
      case "kyc_approval":
      case "kyc_rejection":
        return "bg-indigo-100 text-indigo-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleAuditLogsPageChange = (page: number) => {
    setAuditLogsPage(page);
  };

  const toggleRecentActivity = () => {
    setIsRecentActivityExpanded(!isRecentActivityExpanded);
  };

  const MetricCard = ({ title, value, subtitle, icon, color, index }: any) => (
    <motion.div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{
        scale: 1.02,
        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        transition: { duration: 0.2 },
      }}
    >
      <div className="flex items-center">
        <motion.div
          className={`flex-shrink-0 p-3 rounded-lg ${color}`}
          whileHover={{ rotate: 5 }}
          transition={{ duration: 0.2 }}
        >
          {icon}
        </motion.div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <motion.p
            className="text-2xl font-bold text-gray-900"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
          >
            {value}
          </motion.p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </motion.div>
  );

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-inter"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <MainHeader onBackToDashboard={() => {}} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <motion.h2
            className="text-3xl font-bold text-gray-900 mb-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            Welcome to {capitalize(currentAppMode)} Admin Dashboard !
          </motion.h2>
          <motion.p
            className="text-gray-600"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            Manage your {capitalize(currentAppMode)} trading challenges and user
            accounts from this central dashboard.
          </motion.p>
        </motion.div>

        {/* Metrics Grid */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {[...Array(4)].map((_, i) => (
                <motion.div
                  key={i}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 rounded-lg bg-gray-200">
                      <div className="h-6 w-6 bg-gray-300 rounded"></div>
                    </div>
                    <div className="ml-4">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                      <div className="h-8 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : error ? (
            <motion.div
              className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex">
                <svg
                  className="w-5 h-5 text-red-400 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-red-800">{error}</span>
              </div>
            </motion.div>
          ) : metrics ? (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <MetricCard
                title="Total Users"
                value={metrics?.user_metrics?.total_users || 0}
                subtitle="Registered users"
                icon={
                  <svg
                    className="h-6 w-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                    />
                  </svg>
                }
                color="bg-blue-500"
                index={0}
              />
              <MetricCard
                title="Active Challenges"
                value={metrics?.challenge_metrics?.live_challenges || 0}
                subtitle="Live challenges"
                icon={
                  <svg
                    className="h-6 w-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                }
                color="bg-green-500"
                index={1}
              />
              <MetricCard
                title="Total Revenue"
                value={formatCurrency(metrics.total_revenue || 0)}
                subtitle="This month"
                icon={
                  <svg
                    className="h-6 w-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                    />
                  </svg>
                }
                color="bg-yellow-500"
                index={2}
              />
              <MetricCard
                title="Pending Actions"
                value={metrics.pending_actions || 0}
                subtitle="Requires attention"
                icon={
                  <svg
                    className="h-6 w-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                }
                color="bg-red-500"
                index={3}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* KYC Analytics Section */}
        <motion.div
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <motion.div
            className="flex items-center mb-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <svg
              className="h-6 w-6 text-indigo-600 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17v-2a4 4 0 014-4h2a4 4 0 014 4v2"
              />
            </svg>
            <h3 className="text-lg font-bold text-gray-900">KYC Analytics</h3>
          </motion.div>
          <motion.div
            className="flex flex-wrap gap-4 mb-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              <label className="block text-sm text-gray-600 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={kycStartDate}
                onChange={(e) => setKycStartDate(e.target.value)}
                className="border rounded px-2 py-1"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.8 }}
            >
              <label className="block text-sm text-gray-600 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={kycEndDate}
                onChange={(e) => setKycEndDate(e.target.value)}
                className="border rounded px-2 py-1"
              />
            </motion.div>
            <motion.button
              onClick={() => fetchKycAnalytics(kycStartDate, kycEndDate)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-indigo-700 mt-6"
              disabled={kycLoading}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.9 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {kycLoading ? "Loading..." : "Apply"}
            </motion.button>
          </motion.div>
          {kycLoading ? (
            <motion.div
              className="text-gray-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              Loading KYC analytics...
            </motion.div>
          ) : kycError ? (
            <motion.div
              className="text-red-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {kycError}
            </motion.div>
          ) : kycAnalytics ? (
            <>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-3 gap-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <h4 className="font-semibold text-gray-800 mb-2">Stats</h4>
                  <div className="text-sm text-gray-600">
                    Verified:{" "}
                    <span className="font-bold text-gray-900">
                      {kycAnalytics.kyc_stats?.verified || 0}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Pending:{" "}
                    <span className="font-bold text-gray-900">
                      {kycAnalytics.kyc_stats?.pending || 0}
                    </span>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <h4 className="font-semibold text-gray-800 mb-2 pt-2"> </h4>
                  <div className="text-sm text-gray-600 pt-2">
                    Rejected:{" "}
                    <span className="font-bold text-gray-900">
                      {kycAnalytics.kyc_stats?.rejected || 0}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 pt-2">
                    Not Submitted:{" "}
                    <span className="font-bold text-gray-900">
                      {kycAnalytics.kyc_stats?.not_submitted || 0}
                    </span>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <h4 className="font-semibold text-gray-800 mb-2 pt-2"> </h4>
                  <div className="text-sm text-gray-600 pt-2">
                    Total:{" "}
                    <span className="font-bold text-gray-900">
                      {kycAnalytics.kyc_stats?.total || 0}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 pt-2">
                    KYC Submissions :{" "}
                    <span className="font-bold text-gray-900">
                      {kycAnalytics.kyc_submissions?.length || 0}
                    </span>
                  </div>
                </motion.div>
              </motion.div>
              {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Processing Time (hours)
                  </h4>
                  <div className="text-sm text-gray-600">
                    Average:{" "}
                    <span className="font-bold text-gray-900">
                      {kycAnalytics.processing_time_hours?.average?.toFixed(
                        2
                      ) ?? "N/A"}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Median:{" "}
                    <span className="font-bold text-gray-900">
                      {kycAnalytics.processing_time_hours?.median?.toFixed(2) ??
                        "N/A"}
                    </span>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Volume</h4>
                  <div className="text-sm text-gray-600">
                    Per Day:{" "}
                    <span className="font-bold text-gray-900">
                      {Number(
                        Object.values(
                          kycAnalytics.volume?.per_day || {}
                        ).reduce((a: any, b: any) => a + b, 0)
                      )}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Per Week:{" "}
                    <span className="font-bold text-gray-900">
                      {Number(
                        Object.values(
                          kycAnalytics.volume?.per_week || {}
                        ).reduce((a: any, b: any) => a + b, 0)
                      )}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Per Month:{" "}
                    <span className="font-bold text-gray-900">
                      {Number(
                        Object.values(
                          kycAnalytics.volume?.per_month || {}
                        ).reduce((a: any, b: any) => a + b, 0)
                      )}
                    </span>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Rejection Rate
                  </h4>
                  <div className="text-sm text-gray-600">
                    Rate:{" "}
                    <span className="font-bold text-gray-900">
                      {(kycAnalytics.rejection?.rate * 100)?.toFixed(2)}%
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">Top Reasons:</div>
                  <ul className="list-disc ml-5 text-gray-700">
                    {(kycAnalytics.rejection?.common_reasons || []).map(
                      (r: any, i: number) => (
                        <li key={i}>
                          {r[0]}: {r[1]}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </div> */}
            </>
          ) : null}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 },
            }}
          >
            <div className="flex items-center mb-4">
              <motion.div
                className="flex-shrink-0 p-3 rounded-lg bg-blue-100"
                whileHover={{ rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg
                  className="h-6 w-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
              </motion.div>
              <h3 className="ml-3 text-md font-medium text-gray-900">
                {capitalize(currentAppMode)} Admin User Management
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Manage admin user accounts, roles, and permissions for the admin
              panel.
            </p>
            <motion.button
              onClick={onNavigateToUserManagement}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Manage Admin Users
            </motion.button>
          </motion.div>

          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 },
            }}
          >
            <div className="flex items-center mb-4">
              <motion.div
                className="flex-shrink-0 p-3 rounded-lg bg-green-100"
                whileHover={{ rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </motion.div>
              <h3 className="ml-3 text-md font-medium text-gray-900">
                {capitalize(currentAppMode)} App User Management
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Manage regular app users, KYC, wallet balances, and account
              status.
            </p>
            <motion.button
              onClick={onNavigateToAppUserManagement}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Manage App Users
            </motion.button>
          </motion.div>

          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.0 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 },
            }}
          >
            <div className="flex items-center mb-4">
              <motion.div
                className="flex-shrink-0 p-3 rounded-lg bg-orange-100"
                whileHover={{ rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg
                  className="h-6 w-6 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </motion.div>
              <h3 className="ml-3 text-md font-medium text-gray-900">
                {capitalize(currentAppMode)} KYC Management
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Review and approve KYC documents submitted by app users.
            </p>
            <motion.button
              onClick={onNavigateToKYCManagement}
              className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Review KYC Queue
            </motion.button>
          </motion.div>

          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.1 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 },
            }}
          >
            <div className="flex items-center mb-4">
              <motion.div
                className="flex-shrink-0 p-3 rounded-lg bg-yellow-100"
                whileHover={{ rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </motion.div>
              <h3 className="ml-3 text-md font-medium text-gray-900">
                {capitalize(currentAppMode)} Challenge Management
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Create, edit, and manage trading challenges for users.Customize
              challenge rules and rewards .
            </p>
            <motion.button
              onClick={onNavigateToChallengeManagement}
              className="w-full bg-yellow-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Manage Challenges
            </motion.button>
          </motion.div>

          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.2 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 },
            }}
          >
            <div className="flex items-center mb-4">
              <motion.div
                className="flex-shrink-0 p-3 rounded-lg bg-purple-100"
                whileHover={{ rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg
                  className="h-6 w-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </motion.div>
              <h3 className="ml-3 text-md font-medium text-gray-900">
                {capitalize(currentAppMode)} Audit Logs
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              View and monitor all admin actions and system activities for
              security and compliance.
            </p>
            <motion.button
              onClick={onNavigateToAuditLogs}
              className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              View Audit Logs
            </motion.button>
          </motion.div>

          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.3 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 },
            }}
          >
            <div className="flex items-center mb-4">
              <motion.div
                className="flex-shrink-0 p-3 rounded-lg bg-indigo-100"
                whileHover={{ rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg
                  className="h-6 w-6 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
              </motion.div>
              <h3 className="ml-3 text-md font-medium text-gray-900">
                {capitalize(currentAppMode)} Withdrawal Management
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Review and process user withdrawal requests with TDS calculations
              and bank transfers.
            </p>
            <motion.button
              onClick={onNavigateToWithdrawalManagement}
              className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Review Withdrawals
            </motion.button>
          </motion.div>

          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.4 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 },
            }}
          >
            <div className="flex items-center mb-4">
              <motion.div
                className="flex-shrink-0 p-3 rounded-lg bg-pink-100"
                whileHover={{ rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg
                  className="h-6 w-6 text-pink-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </motion.div>
              <h3 className="ml-3 text-md font-medium text-gray-900">
                {capitalize(currentAppMode)} Banner Management
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Create and manage promotional banners for the mobile app homepage
              with drag-and-drop reordering.
            </p>
            <motion.button
              onClick={onNavigateToBannerManagement}
              className="w-full bg-pink-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Manage Banners
            </motion.button>
          </motion.div>

          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.4 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 },
            }}
          >
            <div className="flex items-center mb-4">
              <motion.div
                className="flex-shrink-0 p-3 rounded-lg bg-pink-100"
                whileHover={{ rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg
                  className="h-6 w-6 text-pink-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </motion.div>
              <h3 className="ml-3 text-md font-medium text-gray-900">
                {capitalize(currentAppMode)} Help & Support
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Design, implement, and manage Help & Support resources and Custom
              Rules for the mobile app.
            </p>
            <motion.button
              onClick={onNavigateToHelpSupport}
              className="w-full bg-pink-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Manage Help & Support
            </motion.button>
          </motion.div>

          <motion.div
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.4 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              transition: { duration: 0.2 },
            }}
          >
            <div className="flex items-center mb-4">
              <motion.div
                className="flex-shrink-0 p-3 rounded-lg bg-pink-100"
                whileHover={{ rotate: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg
                  className="h-6 w-6 text-pink-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </motion.div>
              <h3 className="ml-3 text-md font-medium text-gray-900">
                {capitalize(currentAppMode)} Content Management
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Create and manage about us pages Privacy Policy ,Terms and
              conditions , Custom Rules for the mobile app
            </p>
            <motion.button
              onClick={onNavigateToSportsLegalDocs}
              className="w-full bg-pink-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Manage Legal Pages
            </motion.button>
          </motion.div>

          {isStockTradingMode ? (
            <>
              <motion.div
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.4 }}
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                  transition: { duration: 0.2 },
                }}
              >
                <motion.div
                  className="flex items-center mb-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <motion.div
                    className="flex-shrink-0 p-3 rounded-lg bg-teal-100"
                    whileHover={{ rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg
                      className="h-6 w-6 text-teal-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </motion.div>
                  <h3 className="ml-3 text-md font-medium text-gray-900">
                    Stock Management
                  </h3>
                </motion.div>
                <motion.p
                  className="text-gray-600 mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  Manage Indian stocks data, add new stocks, update prices, and
                  maintain stock information.
                </motion.p>
                <motion.button
                  onClick={onNavigateToStockManagement}
                  className="w-full bg-teal-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all duration-200"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Manage Stocks
                </motion.button>
              </motion.div>
              <motion.div
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.4 }}
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                  transition: { duration: 0.2 },
                }}
              >
                <motion.div
                  className="flex items-center mb-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <motion.div
                    className="flex-shrink-0 p-3 rounded-lg bg-cyan-100"
                    whileHover={{ rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg
                      className="h-6 w-6 text-cyan-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </motion.div>
                  <h3 className="ml-3 text-md font-medium text-gray-900">
                    Stock Universe Management
                  </h3>
                </motion.div>
                <motion.p
                  className="text-gray-600 mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  Create and manage stock universes for challenges, allowing
                  selection of multiple stocks for trading.
                </motion.p>
                <motion.button
                  onClick={onNavigateToStockUniverseManagement}
                  className="w-full bg-cyan-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Manage Stock Universes
                </motion.button>
              </motion.div>{" "}
            </>
          ) : (
            <>
              <motion.div
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.4 }}
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                  transition: { duration: 0.2 },
                }}
              >
                <motion.div
                  className="flex items-center mb-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <motion.div
                    className="flex-shrink-0 p-3 rounded-lg bg-teal-100"
                    whileHover={{ rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg
                      className="h-6 w-6 text-teal-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </motion.div>
                  <h3 className="ml-3 text-md font-medium text-gray-900">
                    Crypto Management
                  </h3>
                </motion.div>
                <motion.p
                  className="text-gray-600 mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  Manage Indian stocks data, add new stocks, update prices, and
                  maintain stock information.
                </motion.p>
                <motion.button
                  onClick={onNavigateToCryptoManagement}
                  className="w-full bg-teal-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all duration-200"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Manage Crypto
                </motion.button>
              </motion.div>

              <motion.div
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.4 }}
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                  transition: { duration: 0.2 },
                }}
              >
                <motion.div
                  className="flex items-center mb-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <motion.div
                    className="flex-shrink-0 p-3 rounded-lg bg-cyan-100"
                    whileHover={{ rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg
                      className="h-6 w-6 text-cyan-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </motion.div>
                  <h3 className="ml-3 text-md font-medium text-gray-900">
                    Crypto Universe Management
                  </h3>
                </motion.div>
                <motion.p
                  className="text-gray-600 mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  Create and manage stock universes for challenges, allowing
                  selection of multiple stocks for trading.
                </motion.p>
                <motion.button
                  onClick={onNavigateToCryptoUniverseManagement}
                  className="w-full bg-cyan-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Manage Crypto Universes
                </motion.button>
              </motion.div>
            </>
          )}
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.5 }}
        >
          <motion.div
            className="px-6 py-4 border-b border-gray-200"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.6 }}
          >
            <div
              className="flex items-center justify-between"
              style={{ cursor: "pointer" }}
              onClick={toggleRecentActivity}
            >
              <div className="flex items-center space-x-3">
                <motion.div
                  className="flex-shrink-0 p-2 rounded-lg bg-blue-100"
                  whileHover={{ rotate: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </motion.div>
                <h3 className="text-lg font-medium text-gray-900">
                  Recent Activity
                </h3>
              </div>
              <motion.button
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label={
                  isRecentActivityExpanded
                    ? "Collapse Recent Activity"
                    : "Expand Recent Activity"
                }
              >
                <motion.svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  animate={{ rotate: isRecentActivityExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </motion.svg>
              </motion.button>
            </div>
          </motion.div>
          <AnimatePresence>
            {isRecentActivityExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="p-6">
                  {recentActivityLoading ? (
                    <motion.div
                      className="text-center text-gray-500 py-8"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                      <p>Loading recent activity...</p>
                    </motion.div>
                  ) : recentActivityError ? (
                    <motion.div
                      className="text-center text-red-500 py-8"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p>{recentActivityError}</p>
                    </motion.div>
                  ) : recentActivityData &&
                    recentActivityData.logs &&
                    recentActivityData.logs.length > 0 ? (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {/* Audit Logs Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full  divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Admin
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Action
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Resource
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Details
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Timestamp
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {recentActivityData.logs.map(
                              (log: any, index: number) => (
                                <motion.tr
                                  key={log.id}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{
                                    duration: 0.3,
                                    delay: index * 0.05,
                                  }}
                                  className="hover:bg-gray-50"
                                >
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {log.admin_email}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionBadgeColor(
                                        log.action
                                      )}`}
                                    >
                                      {log.action}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getResourceTypeBadgeColor(
                                        log.resource_type
                                      )}`}
                                    >
                                      {log.resource_type.replace(/_/g, " ")}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                                    <div className="truncate">
                                      {log.details &&
                                      typeof log.details === "object"
                                        ? Object.entries(log.details)
                                            .map(
                                              ([key, value]) =>
                                                `${key}: ${value}`
                                            )
                                            .join(", ")
                                        : log.details || "-"}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        log.success
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {log.success ? "Success" : "Failed"}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatTimestamp(log.timestamp)}
                                  </td>
                                </motion.tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {recentActivityData.total_pages > 1 && (
                        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4">
                          <div className="flex-1 flex justify-between sm:hidden">
                            <button
                              onClick={() =>
                                handleAuditLogsPageChange(
                                  Math.max(1, auditLogsPage - 1)
                                )
                              }
                              disabled={auditLogsPage === 1}
                              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <button
                              onClick={() =>
                                handleAuditLogsPageChange(
                                  Math.min(
                                    recentActivityData.total_pages,
                                    auditLogsPage + 1
                                  )
                                )
                              }
                              disabled={
                                auditLogsPage === recentActivityData.total_pages
                              }
                              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm text-gray-700">
                                Showing{" "}
                                <span className="font-medium">
                                  {(auditLogsPage - 1) * auditLogsPerPage + 1}
                                </span>{" "}
                                to{" "}
                                <span className="font-medium">
                                  {Math.min(
                                    auditLogsPage * auditLogsPerPage,
                                    recentActivityData.total
                                  )}
                                </span>{" "}
                                of{" "}
                                <span className="font-medium">
                                  {recentActivityData.total}
                                </span>{" "}
                                results
                              </p>
                            </div>
                            <div>
                              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                <button
                                  onClick={() =>
                                    handleAuditLogsPageChange(
                                      Math.max(1, auditLogsPage - 1)
                                    )
                                  }
                                  disabled={auditLogsPage === 1}
                                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <svg
                                    className="h-5 w-5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                                {Array.from(
                                  {
                                    length: Math.min(
                                      5,
                                      recentActivityData.total_pages
                                    ),
                                  },
                                  (_, i) => {
                                    const page =
                                      Math.max(
                                        1,
                                        Math.min(
                                          recentActivityData.total_pages - 4,
                                          auditLogsPage - 2
                                        )
                                      ) + i;
                                    return (
                                      <button
                                        key={page}
                                        onClick={() =>
                                          handleAuditLogsPageChange(page)
                                        }
                                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                          page === auditLogsPage
                                            ? "z-10 bg-indigo-50 border-indigo-500 text-indigo-600"
                                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                        }`}
                                      >
                                        {page}
                                      </button>
                                    );
                                  }
                                )}
                                <button
                                  onClick={() =>
                                    handleAuditLogsPageChange(
                                      Math.min(
                                        recentActivityData.total_pages,
                                        auditLogsPage + 1
                                      )
                                    )
                                  }
                                  disabled={
                                    auditLogsPage ===
                                    recentActivityData.total_pages
                                  }
                                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <svg
                                    className="h-5 w-5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </nav>
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      className="text-center text-gray-500 py-8"
                      initial={{ opacity: 0, scale: 1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 1.6 }}
                    >
                      <motion.svg
                        className="w-12 h-12 mx-auto mb-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        initial={{ rotate: -10 }}
                        animate={{ rotate: 0 }}
                        transition={{ duration: 0.5, delay: 1.7 }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </motion.svg>
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 1.8 }}
                      >
                        No recent activity to display
                      </motion.p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}
